import React, { useEffect, useState } from 'react';
import { AdminShell } from '@/components/layout/AdminShell';
import { Card } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { StatusBadge } from '@/components/StatusBadge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { api, downloadFile } from '@/lib/api';
import { formatNumber, formatDateTime } from '@/lib/format';
import { exportToCsv } from '@/lib/csv';
import { Eye, Download, Crown, Diamond, Search } from 'lucide-react';
import { toast } from 'sonner';

function DateRange({ start, end, setStart, setEnd, testidPrefix }) {
    return (
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            From <Input type="date" value={start} max={end || undefined} onChange={e => setStart(e.target.value)} className="h-9 w-36" data-testid={`${testidPrefix}-start-date`} />
            To <Input type="date" value={end} min={start || undefined} onChange={e => setEnd(e.target.value)} className="h-9 w-36" data-testid={`${testidPrefix}-end-date`} />
            {(start || end) && <Button variant="ghost" size="sm" onClick={() => { setStart(''); setEnd(''); }} data-testid={`${testidPrefix}-clear-dates`}>Clear</Button>}
        </div>
    );
}

export default function AdminLogsPage() {
    // Bridge (user) logs
    const [logs, setLogs] = useState([]);
    const [platform, setPlatform] = useState('all');
    const [status, setStatus] = useState('all');
    const [kind, setKind] = useState('all');
    const [via, setVia] = useState('all');
    const [bStart, setBStart] = useState('');
    const [bEnd, setBEnd] = useState('');
    const [bSearch, setBSearch] = useState('');

    // Reseller recharges
    const [recharges, setRecharges] = useState([]);
    const [resellers, setResellers] = useState([]);
    const [resellerId, setResellerId] = useState('all');
    const [rStart, setRStart] = useState('');
    const [rEnd, setREnd] = useState('');

    // Allocation ledger
    const [refills, setRefills] = useState([]);
    const [aStart, setAStart] = useState('');
    const [aEnd, setAEnd] = useState('');

    useEffect(() => {
        const p = new URLSearchParams({ initiator: 'user' });
        if (platform !== 'all') p.set('platform', platform);
        if (status !== 'all') p.set('status', status);
        if (kind !== 'all') p.set('kind', kind);
        if (via !== 'all') p.set('via', via);
        if (bStart) p.set('start_date', bStart);
        if (bEnd) p.set('end_date', bEnd);
        api.get(`/admin/recharge-logs?${p.toString()}`).then(r => setLogs(r.data.logs || []));
    }, [platform, status, kind, via, bStart, bEnd]);

    useEffect(() => {
        api.get('/admin/resellers').then(r => setResellers(r.data.resellers || r.data || [])).catch(() => {});
    }, []);

    useEffect(() => {
        const p = new URLSearchParams();
        if (resellerId !== 'all') p.set('reseller_id', resellerId);
        if (rStart) p.set('start_date', rStart);
        if (rEnd) p.set('end_date', rEnd);
        api.get(`/admin/recharges?${p.toString()}`).then(r => setRecharges(r.data || []));
    }, [resellerId, rStart, rEnd]);

    useEffect(() => {
        const p = new URLSearchParams();
        if (aStart) p.set('start_date', aStart);
        if (aEnd) p.set('end_date', aEnd);
        api.get(`/admin/refills?${p.toString()}`).then(r => setRefills(r.data.refills || []));
    }, [aStart, aEnd]);

    const getInvoice = async (paymentId) => {
        try { await downloadFile(`/admin/invoice/${paymentId}`, `invoice-${paymentId}.pdf`); }
        catch { toast.error('Invoice not available for this recharge'); }
    };

    const filteredLogs = logs.filter(l => {
        const q = bSearch.trim().toLowerCase();
        if (!q) return true;
        return String(l.in_app_id || '').toLowerCase().includes(q)
            || String(l.user_name || '').toLowerCase().includes(q)
            || String(l.user_mobile || '').toLowerCase().includes(q);
    });

    const exportBridge = () => exportToCsv('user-recharge-logs', filteredLogs, [
        { header: 'Time', accessor: l => formatDateTime(l.created_at) },
        { header: 'Type', accessor: l => (l.kind === 'noble' ? `Noble: ${l.noble_tier_label || ''}` : 'Diamond transfer') },
        { header: 'Source', accessor: l => (l.via === 'stock' ? 'User stock' : 'Paid') },
        { header: 'User', accessor: l => l.user_name },
        { header: 'Mobile', accessor: l => l.user_mobile },
        { header: 'Platform', accessor: l => l.platform },
        { header: 'In-app ID', accessor: l => l.in_app_id },
        { header: 'Amount', accessor: l => l.amount },
        { header: 'Status', accessor: l => l.status },
        { header: 'Message', accessor: l => l.message },
    ]);
    const exportReseller = () => exportToCsv('reseller-recharges', recharges, [
        { header: 'Time', accessor: r => formatDateTime(r.created_at) },
        { header: 'Reseller', accessor: r => r.reseller_name },
        { header: 'Email', accessor: r => r.reseller_email },
        { header: 'Platform', accessor: r => r.platform },
        { header: 'In-app ID', accessor: r => r.recipient_uid || r.in_app_id },
        { header: 'Diamonds', accessor: r => r.diamonds },
        { header: 'Status', accessor: r => r.status },
    ]);
    const exportRefills = () => exportToCsv('allocation-ledger', refills, [
        { header: 'Time', accessor: r => formatDateTime(r.created_at) },
        { header: 'Type', accessor: r => (r.kind === 'clawback' || (r.amount || 0) < 0 ? 'Clawback' : 'Refill') },
        { header: 'Reseller', accessor: r => r.reseller_email },
        { header: 'Platform', accessor: r => r.platform },
        { header: 'Change', accessor: r => r.amount },
        { header: 'By admin', accessor: r => r.by_admin_email },
        { header: 'Reason', accessor: r => r.reason },
        { header: 'Note', accessor: r => r.note },
    ]);

    return (
        <AdminShell title="Recharge Logs">
            <Tabs defaultValue="bridge">
                <TabsList>
                    <TabsTrigger value="bridge" data-testid="admin-logs-tab-bridge">Bridge Logs (Users)</TabsTrigger>
                    <TabsTrigger value="transfers" data-testid="admin-logs-tab-transfers">Reseller Recharges</TabsTrigger>
                    <TabsTrigger value="refills" data-testid="admin-logs-tab-refills">Allocation Ledger</TabsTrigger>
                </TabsList>

                <TabsContent value="bridge" className="mt-4">
                    <Card className="p-4 sm:p-5">
                        <div className="flex flex-wrap gap-3 mb-3 items-center">
                            <Select value={platform} onValueChange={setPlatform}><SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
                                <SelectContent><SelectItem value="all">All platforms</SelectItem>{['olaparty','trumeet'].map(p => <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>)}</SelectContent></Select>
                            <Select value={status} onValueChange={setStatus}><SelectTrigger className="w-44"><SelectValue /></SelectTrigger>
                                <SelectContent>{['all','SUCCESS','PENDING_MANUAL','FAILED','FULFILLED_ADMIN'].map(s => <SelectItem key={s} value={s} className="capitalize">{s.replace(/_/g, ' ')}</SelectItem>)}</SelectContent></Select>
                            <Select value={kind} onValueChange={setKind}><SelectTrigger className="w-48" data-testid="admin-bridge-kind-filter"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">All types</SelectItem>
                                    <SelectItem value="diamond">Diamond transfer</SelectItem>
                                    <SelectItem value="noble">Noble recharge</SelectItem>
                                </SelectContent></Select>
                            <Select value={via} onValueChange={setVia}><SelectTrigger className="w-44" data-testid="admin-bridge-source-filter"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">All sources</SelectItem>
                                    <SelectItem value="paid">Paid (gateway)</SelectItem>
                                    <SelectItem value="stock">From user stock</SelectItem>
                                </SelectContent></Select>
                            <DateRange start={bStart} end={bEnd} setStart={setBStart} setEnd={setBEnd} testidPrefix="admin-bridge" />
                            <div className="relative w-56">
                                <Input value={bSearch} onChange={e => setBSearch(e.target.value)} placeholder="Search in-app ID / name / WhatsApp…" className="h-9 pl-8" data-testid="admin-bridge-search-input" />
                                <Search className="h-4 w-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                            </div>
                            <Button variant="outline" size="sm" className="ml-auto" onClick={exportBridge} data-testid="admin-bridge-export-csv"><Download className="h-4 w-4 mr-1" /> CSV</Button>
                        </div>
                        <Table>
                            <TableHeader><TableRow><TableHead>Time</TableHead><TableHead>Type</TableHead><TableHead>Source</TableHead><TableHead>Initiator</TableHead><TableHead>Platform</TableHead><TableHead>In-app ID</TableHead><TableHead>Amount</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Details</TableHead></TableRow></TableHeader>
                            <TableBody>
                                {filteredLogs.map(l => (
                                    <TableRow key={l.id}>
                                        <TableCell className="whitespace-nowrap">{formatDateTime(l.created_at)}</TableCell>
                                        <TableCell>
                                            {l.kind === 'noble' ? (
                                                <span className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full bg-amber-50 text-amber-800 border border-amber-200" data-testid={`admin-bridge-type-${l.id}`}>
                                                    <Crown className="h-3 w-3" /> {l.noble_tier_label || 'Noble'}
                                                </span>
                                            ) : (
                                                <span className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full bg-sky-50 text-sky-700 border border-sky-200" data-testid={`admin-bridge-type-${l.id}`}>
                                                    <Diamond className="h-3 w-3" /> Diamond
                                                </span>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {l.via === 'stock'
                                                ? <span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-violet-50 text-violet-700 border border-violet-200" data-testid={`admin-bridge-source-${l.id}`}>User stock</span>
                                                : <span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200" data-testid={`admin-bridge-source-${l.id}`}>Paid</span>}
                                        </TableCell>
                                        <TableCell>
                                            <div className="font-medium">{l.user_name || '—'}</div>
                                            {l.user_mobile && <div className="text-xs text-muted-foreground font-mono">{l.user_mobile}</div>}
                                        </TableCell>
                                        <TableCell className="capitalize">{l.platform}</TableCell>
                                        <TableCell className="font-mono text-xs">{l.in_app_id}</TableCell>
                                        <TableCell className="tabular-nums">{formatNumber(l.amount)}</TableCell>
                                        <TableCell><StatusBadge status={l.status} /></TableCell>
                                        <TableCell className="text-right">
                                            <Dialog><DialogTrigger asChild><Button variant="ghost" size="sm" data-testid={`admin-bridge-details-${l.id}`}><Eye className="h-4 w-4" /></Button></DialogTrigger>
                                                <DialogContent className="max-w-2xl">
                                                    <DialogHeader><DialogTitle>Recharge details</DialogTitle></DialogHeader>
                                                    <div className="text-xs space-y-2">
                                                        <div><span className="font-semibold">User:</span> {l.user_name} {l.user_mobile ? `(${l.user_mobile})` : ''}</div>
                                                        <div><span className="font-semibold">Message:</span> {l.message}</div>
                                                        {l.via === 'stock' && <div><span className="font-semibold">Stock deducted:</span> {formatNumber(l.base_diamonds)} 💎{l.amount_inr != null ? ` (₹${l.amount_inr})` : ''}{l.balance_after != null ? ` · balance after ${formatNumber(l.balance_after)}` : ''}</div>}
                                                        {l.client_ip && <div><span className="font-semibold">IP / browser:</span> <span className="font-mono">{l.client_ip}</span> · {l.client_browser} · {l.client_os}</div>}
                                                        <div><span className="font-semibold">Source ref:</span> <span className="font-mono">{l.source_ref}</span></div>
                                                        {l.invoice_available && l.payment_id && (
                                                            <Button size="sm" variant="outline" onClick={() => getInvoice(l.payment_id)} data-testid={`admin-bridge-invoice-${l.id}`}>
                                                                <Download className="h-3.5 w-3.5 mr-1" /> Download invoice
                                                            </Button>
                                                        )}
                                                        <div className="bg-secondary rounded p-2 font-mono text-[11px] whitespace-pre-wrap break-words max-h-72 overflow-y-auto">{JSON.stringify({ request: l.request, response: l.response }, null, 2)}</div>
                                                    </div>
                                                </DialogContent>
                                            </Dialog>
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {filteredLogs.length === 0 && <TableRow><TableCell colSpan={9} className="text-center text-muted-foreground py-8">{bSearch.trim() ? 'No recharges match that in-app ID.' : 'No user recharges.'}</TableCell></TableRow>}
                            </TableBody>
                        </Table>
                    </Card>
                </TabsContent>

                <TabsContent value="transfers" className="mt-4">
                    <Card className="p-4 sm:p-5">
                        <div className="flex flex-wrap gap-3 mb-3 items-center">
                            <Select value={resellerId} onValueChange={setResellerId}><SelectTrigger className="w-60" data-testid="admin-reseller-filter"><SelectValue placeholder="All resellers" /></SelectTrigger>
                                <SelectContent><SelectItem value="all">All resellers</SelectItem>{resellers.map(r => <SelectItem key={r.id} value={r.id}>{r.name || r.email}</SelectItem>)}</SelectContent></Select>
                            <DateRange start={rStart} end={rEnd} setStart={setRStart} setEnd={setREnd} testidPrefix="admin-reseller" />
                            <Button variant="outline" size="sm" className="ml-auto" onClick={exportReseller} data-testid="admin-reseller-export-csv"><Download className="h-4 w-4 mr-1" /> CSV</Button>
                        </div>
                        <Table>
                            <TableHeader><TableRow><TableHead>Time</TableHead><TableHead>Reseller</TableHead><TableHead>Platform</TableHead><TableHead>Recipient</TableHead><TableHead>Diamonds</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
                            <TableBody>{recharges.map(t => (
                                <TableRow key={t.id}>
                                    <TableCell className="whitespace-nowrap">{formatDateTime(t.created_at)}</TableCell>
                                    <TableCell><div className="font-medium">{t.reseller_name || '—'}</div><div className="text-xs text-muted-foreground">{t.reseller_email}</div></TableCell>
                                    <TableCell className="capitalize">{t.platform}</TableCell>
                                    <TableCell className="font-mono text-xs">{t.recipient_uid || t.in_app_id}</TableCell>
                                    <TableCell className="tabular-nums">{formatNumber(t.diamonds)}</TableCell>
                                    <TableCell><StatusBadge status={t.status} /></TableCell>
                                </TableRow>
                            ))}{recharges.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">No reseller recharges.</TableCell></TableRow>}</TableBody>
                        </Table>
                    </Card>
                </TabsContent>

                <TabsContent value="refills" className="mt-4">
                    <Card className="p-4 sm:p-5">
                        <div className="flex flex-wrap gap-3 mb-3 items-center">
                            <DateRange start={aStart} end={aEnd} setStart={setAStart} setEnd={setAEnd} testidPrefix="admin-alloc" />
                            <Button variant="outline" size="sm" className="ml-auto" onClick={exportRefills} data-testid="admin-alloc-export-csv"><Download className="h-4 w-4 mr-1" /> CSV</Button>
                        </div>
                        <Table>
                            <TableHeader><TableRow><TableHead>Time</TableHead><TableHead>Type</TableHead><TableHead>Reseller</TableHead><TableHead>Platform</TableHead><TableHead className="text-right">Change</TableHead><TableHead>By admin</TableHead><TableHead>Reason / Note</TableHead></TableRow></TableHeader>
                            <TableBody>{refills.map(r => {
                                const isClawback = r.kind === 'clawback' || (typeof r.amount === 'number' && r.amount < 0);
                                const absAmount = Math.abs(r.amount || 0);
                                return (
                                    <TableRow key={r.id} data-testid={`admin-allocation-log-${r.id}`}>
                                        <TableCell className="whitespace-nowrap">{formatDateTime(r.created_at)}</TableCell>
                                        <TableCell><span className={`text-[11px] uppercase tracking-wider font-medium px-2 py-0.5 rounded-full ${isClawback ? 'bg-rose-50 text-rose-700 border border-rose-200' : 'bg-emerald-50 text-emerald-700 border border-emerald-200'}`}>{isClawback ? 'Clawback' : 'Refill'}</span></TableCell>
                                        <TableCell>{r.reseller_email}</TableCell>
                                        <TableCell className="capitalize">{r.platform}</TableCell>
                                        <TableCell className={`tabular-nums text-right font-medium ${isClawback ? 'text-rose-700' : 'text-emerald-700'}`}>{isClawback ? '−' : '+'}{formatNumber(absAmount)}</TableCell>
                                        <TableCell>{r.by_admin_email}</TableCell>
                                        <TableCell className="text-muted-foreground text-xs">{r.reason && <span className="font-medium text-foreground/80">{r.reason}</span>}{r.reason && r.note && <span> · </span>}{r.note}</TableCell>
                                    </TableRow>
                                );
                            })}{refills.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-8">No allocation changes.</TableCell></TableRow>}</TableBody>
                        </Table>
                    </Card>
                </TabsContent>
            </Tabs>
        </AdminShell>
    );
}
